import { queryProStudio } from '@pypestream/api-services';
import { useEffect, useState } from 'react';

import type { Channel } from '../utils/get-channels-from-pypes';
import { getProjectChannels } from '../utils/get-project-channels';

/**
 * What each solution card on Home says beyond its name: where it runs, what
 * it runs on, how big it is (prototype `renderHome`, `.sol-card`). The
 * solutions list query carries only names, so this is read separately, for
 * the whole project at once — two requests, not one per card — and every
 * fact is optional: a card never waits on it, and one that fails to load
 * just shows the name.
 */
export interface SolutionCardFacts {
  updatedAt?: string;
  /** Channels pointing at this solution, by kind. */
  channels: { pype: number; voice: number };
  /** Environments it has a channel in, Live first. */
  envs: ('live' | 'sandbox')[];
}

export interface CardSolution {
  id: string;
  botSolutionId?: string | null;
}

export interface VersionRow {
  id: string;
  solutionId: string;
  updatedAt?: string | null;
}

// Versions, not solutions: every environment's schema has these fields.
const VERSIONS_QUERY = `query HomeSolutionCards($ids: [String!]!) {
  tenancy_solution_versions(where: { solutionId: { _in: $ids } }) {
    id
    solutionId
    updatedAt
  }
}`;

/** Each solution's most recently updated version: the draft being worked on. */
export const latestVersions = (rows: VersionRow[]) => {
  const latest = new Map<string, VersionRow>();
  rows.forEach((r) => {
    const cur = latest.get(r.solutionId);
    if (!cur || (r.updatedAt ?? '') > (cur.updatedAt ?? '')) {
      latest.set(r.solutionId, r);
    }
  });
  return latest;
};

/** Channels of one kind, counted once however many environments they're in. */
const distinct = (channels: Channel[], type: Channel['type']) =>
  new Set(channels.filter((c) => c.type === type).map((c) => c.name || c.id))
    .size;

/** Pure: put the two reads together per solution. */
export const summarizeCardFacts = ({
  solutions,
  rows,
  channels,
}: {
  solutions: CardSolution[];
  rows: VersionRow[];
  channels: Channel[];
}): Map<string, SolutionCardFacts> => {
  const latest = latestVersions(rows);

  return new Map(
    solutions.map((s) => {
      const row = latest.get(s.id);
      const mine = s.botSolutionId
        ? channels.filter((c) => c.botId === s.botSolutionId)
        : [];
      const envs = (['live', 'sandbox'] as const).filter((e) =>
        mine.some((c) => c.env === e)
      );
      return [
        s.id,
        {
          updatedAt: row?.updatedAt ?? undefined,
          channels: {
            pype: distinct(mine, 'widget'),
            voice: distinct(mine, 'twilio_voice'),
          },
          envs,
        },
      ];
    })
  );
};

const readVersionRows = async (ids: string[]): Promise<VersionRow[]> => {
  const res = await queryProStudio<{
    tenancy_solution_versions?: VersionRow[];
  }>(VERSIONS_QUERY, { ids });
  if (res.error) throw res.error;
  return res.data?.tenancy_solution_versions ?? [];
};

export const useSolutionCardFacts = ({
  solutions,
  projectId,
  uniqueCustomerId,
  enabled,
}: {
  solutions: CardSolution[];
  projectId?: string;
  uniqueCustomerId?: string;
  enabled: boolean;
}) => {
  const [facts, setFacts] = useState<Map<string, SolutionCardFacts>>(new Map());
  const key = solutions.map((s) => s.id).join(',');

  useEffect(() => {
    if (!enabled || !solutions.length) return;
    let cancelled = false;
    (async () => {
      const [rows, channels] = await Promise.all([
        readVersionRows(solutions.map((s) => s.id)).catch((err) => {
          console.warn('[home] solution details unavailable', err);
          return [] as VersionRow[];
        }),
        projectId && uniqueCustomerId
          ? getProjectChannels({ projectId, uniqueCustomerId }).catch((err) => {
              console.warn('[home] channels unavailable', err);
              return [] as Channel[];
            })
          : Promise.resolve([] as Channel[]),
      ]);
      if (cancelled) return;
      setFacts(summarizeCardFacts({ solutions, rows, channels }));
    })();
    return () => {
      cancelled = true;
    };
    // `key` stands for `solutions`: a new array each render, the same ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, projectId, uniqueCustomerId]);

  return facts;
};

/** "Updated 2 hours ago", "Updated yesterday", "Updated Sep 12". */
export const updatedLabel = (iso: string | undefined, now = Date.now()) => {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return null;
  const mins = Math.round((now - at) / 60_000);
  if (mins < 1) return 'Updated just now';
  if (mins < 60) return `Updated ${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Updated ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Updated yesterday';
  if (days < 7) return `Updated ${days} days ago`;
  return `Updated ${new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(new Date(at).getFullYear() !== new Date(now).getFullYear()
      ? { year: 'numeric' }
      : {}),
  })}`;
};
